import { useState, useCallback, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle, AlertCircle, Loader2, ShieldAlert, Users, Briefcase, Megaphone, GraduationCap, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { submitApplication, committees } from '@/lib/api';
import { useLanguage } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

// Constants
const turkeyProvinces = [
    'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Aksaray', 'Amasya', 'Ankara', 'Antalya', 'Ardahan', 'Artvin',
    'Aydın', 'Balıkesir', 'Bartın', 'Batman', 'Bayburt', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur',
    'Bursa', 'Çanakkale', 'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Düzce', 'Edirne', 'Elazığ', 'Erzincan',
    'Erzurum', 'Eskişehir', 'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkari', 'Hatay', 'Iğdır', 'Isparta', 'İstanbul',
    'İzmir', 'Kahramanmaraş', 'Karabük', 'Karaman', 'Kars', 'Kastamonu', 'Kayseri', 'Kırıkkale', 'Kırklareli', 'Kırşehir',
    'Kilis', 'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa', 'Mardin', 'Mersin', 'Muğla', 'Muş',
    'Nevşehir', 'Niğde', 'Ordu', 'Osmaniye', 'Rize', 'Sakarya', 'Samsun', 'Şanlıurfa', 'Siirt', 'Sinop',
    'Şırnak', 'Sivas', 'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Uşak', 'Van', 'Yalova', 'Yozgat', 'Zonguldak'
];

const applicationTypes = [
    { id: 'delegate', icon: Users, label: 'Delege Başvurusu', labelEn: 'Delegate Application' },
    { id: 'organization', icon: Briefcase, label: 'Organizasyon Başvurusu', labelEn: 'Organization Application' },
    { id: 'pr', icon: Megaphone, label: 'PR Başvurusu', labelEn: 'PR Application' },
    { id: 'academy', icon: GraduationCap, label: 'Akademi Başvurusu', labelEn: 'Academy Application' }
];

const organizationPositions = ['Entertainment', 'Security', 'Press', 'Staff'];
const academyRoles = ['USG', 'Chair', 'Co-Chair', 'Rapporteur', 'Crisis Director', 'Academic Assistant'];
const hearAboutOptions = ['Instagram', 'TikTok', 'Okul', 'Arkadaş', 'Diğer'];
const conferenceCountOptions = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '10+'];
const grades = [
    { value: '9', label: '9. Sınıf' },
    { value: '10', label: '10. Sınıf' },
    { value: '11', label: '11. Sınıf' },
    { value: '12', label: '12. Sınıf' },
    { value: 'graduate', label: 'Mezun' }
];

// Schemas
const baseSchema = {
    firstName: z.string().min(2, 'Ad en az 2 karakter olmalı'),
    lastName: z.string().min(2, 'Soyad en az 2 karakter olmalı'),
    email: z.string().email('Geçerli bir e-posta adresi girin'),
    phone: z.string().min(10, 'Geçerli bir telefon numarası girin'),
    dateOfBirth: z.string().min(1, 'Doğum tarihi gerekli'),
    city: z.string().min(1, 'İl seçiniz'),
    district: z.string().min(2, 'İlçe gerekli'),
    schoolName: z.string().min(2, 'Okul adı en az 2 karakter olmalı'),
    grade: z.string().min(1, 'Sınıf seçiniz'),
    parentName: z.string().min(2, 'Veli adı gerekli'),
    parentPhone: z.string().min(10, 'Geçerli bir telefon numarası girin'),
    hasChronicIllness: z.string().min(1, 'Lütfen seçim yapın'),
    allergies: z.string().optional(),
    emergencyMedications: z.string().optional(),
    kvkkConsent: z.boolean().refine(val => val === true, 'KVKK metnini kabul etmelisiniz'),
    guardianConsent: z.boolean().refine(val => val === true, 'Veli onayı gerekli')
};

const schemas = {
    delegate: z.object({
        ...baseSchema,
        committee1: z.string().min(1, '1. tercih seçiniz'),
        committee2: z.string().min(1, '2. tercih seçiniz'),
        committee3: z.string().min(1, '3. tercih seçiniz'),
    }).refine((data) => new Set([data.committee1, data.committee2, data.committee3]).size === 3, { message: "Komite tercihleri farklı olmalı", path: ["committee3"] }),
    organization: z.object({ ...baseSchema, position: z.string().min(1, 'Pozisyon seçiniz'), previousConferences: z.string().optional() }),
    pr: z.object({ ...baseSchema, previousConferences: z.string().optional(), motivationLetter: z.string().min(50, 'Motivasyon mektubu en az 50 karakter olmalı'), hearAbout: z.string().min(1, 'Lütfen seçim yapın') }),
    academy: z.object({ ...baseSchema, conferenceCount: z.string().min(1, 'Katılım sayısı seçiniz'), roleType: z.string().min(1, 'Görev türü seçiniz'), conferenceList: z.string().optional() })
};

const DRAFT_KEY = 'girnemun_application_draft';

const defaultValues = {
    firstName: '', lastName: '', email: '', phone: '', dateOfBirth: '',
    city: '', district: '', schoolName: '', grade: '',
    committee1: '', committee2: '', committee3: '',
    parentName: '', parentPhone: '',
    hasChronicIllness: '', allergies: '', emergencyMedications: '',
    kvkkConsent: false, guardianConsent: false,
    position: '', previousConferences: '', motivationLetter: '', hearAbout: '',
    conferenceCount: '', roleType: '', conferenceList: ''
};

// Sub-components for cleaner structure
const PageHeader = ({ view, applicationType, onBack, language }) => {
    const currentTypeInfo = applicationTypes.find(t => t.id === applicationType);
    return (
        <section className="pt-32 pb-12 bg-primary">
            <div className="max-w-7xl mx-auto px-4">
                <div className="max-w-3xl">
                    {view === 'form' && (
                        <button type="button" onClick={onBack} className="text-accent hover:text-accent/80 text-sm mb-4 flex items-center gap-2" data-testid="back-to-types">
                            ← Başvuru Türünü Değiştir
                        </button>
                    )}
                    <p className="text-accent font-semibold text-sm uppercase tracking-wider mb-4">
                        {view === 'form' ? currentTypeInfo?.label : view === 'success' ? 'TAMAMLANDI' : 'BAŞVURU'}
                    </p>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold text-primary-foreground mb-6">
                        {view === 'form' ? 'Başvuru Formu' : view === 'success' ? 'Başvuru Tamamlandı' : "Girne MUN 2026'ya Katılın"}
                    </h1>
                    {view === 'selection' && (
                        <p className="text-xl text-primary-foreground/80">Başvuru türünüzü seçerek forma devam edin.</p>
                    )}
                </div>
            </div>
        </section>
    );
};

const SuccessView = ({ onReset }) => (
    <div className="max-w-2xl mx-auto px-4 text-center">
        <div className="bg-card rounded-xl border border-border p-12">
            <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="font-display text-3xl font-bold text-foreground mb-4">Başvurunuz Alındı!</h2>
            <p className="text-muted-foreground mb-8">Başvurunuz başarıyla gönderildi. En kısa sürede sizinle iletişime geçeceğiz.</p>
            <Button onClick={onReset} className="btn-navy" data-testid="new-application-btn">Yeni Başvuru</Button>
        </div>
    </div>
);

const SelectionView = ({ onSelect, hasDraft, draftType, onContinueDraft, onDeleteDraft, language }) => (
    <div className="max-w-4xl mx-auto px-4">
        {hasDraft && (
            <div className="mb-8 p-4 rounded-xl bg-accent/10 border border-accent/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <p className="font-medium text-foreground">{language === 'tr' ? 'Kaydedilmiş taslağınız var!' : 'You have a saved draft!'}</p>
                    <p className="text-sm text-muted-foreground">
                        {language === 'tr' ? `${applicationTypes.find(t => t.id === draftType)?.label || 'Başvuru'} taslağınız kaldığı yerden devam edebilir.` : `Your ${applicationTypes.find(t => t.id === draftType)?.labelEn || 'application'} draft can be continued.`}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={onContinueDraft} className="btn-premium" data-testid="continue-draft-btn">{language === 'tr' ? 'Devam Et' : 'Continue'}</Button>
                    <Button onClick={onDeleteDraft} variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10" data-testid="delete-draft-btn"><Trash2 className="w-4 h-4" /></Button>
                </div>
            </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {applicationTypes.map((type) => {
                const Icon = type.icon;
                return (
                    <button key={type.id} type="button" onClick={() => onSelect(type.id)} data-testid={`apply-type-${type.id}`}
                        className="p-8 rounded-xl bg-card border border-border hover:border-accent/50 transition-all duration-300 hover:shadow-elegant-lg text-left group">
                        <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                            <Icon className="w-7 h-7 text-accent" />
                        </div>
                        <h3 className="font-display text-xl font-bold text-foreground mb-2">{language === 'tr' ? type.label : type.labelEn}</h3>
                    </button>
                );
            })}
        </div>
    </div>
);

/**
 * ApplyPage - Pure component without Layout wrapper
 * Layout is handled at route level via App.js
 */
const ApplyPage = () => {
    const { language } = useLanguage();
    const [view, setView] = useState('selection');
    const [applicationType, setApplicationType] = useState('delegate');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [selectedCommittees, setSelectedCommittees] = useState({ committee1: '', committee2: '', committee3: '' });
    const [hasDraft, setHasDraft] = useState(false);
    const [draftType, setDraftType] = useState(null);

    const schema = useMemo(() => schemas[applicationType] || schemas.delegate, [applicationType]);
    const { register, handleSubmit, setValue, reset, getValues, formState: { errors } } = useForm({
        resolver: zodResolver(schema),
        defaultValues
    });

    useEffect(() => {
        try {
            const saved = localStorage.getItem(DRAFT_KEY);
            if (saved) {
                const draft = JSON.parse(saved);
                if (draft.applicationType && draft.formData) {
                    setHasDraft(true);
                    setDraftType(draft.applicationType);
                }
            }
        } catch (e) {
            localStorage.removeItem(DRAFT_KEY);
        }
    }, []);

    const handleTypeSelect = useCallback((type) => {
        setApplicationType(type);
        setSelectedCommittees({ committee1: '', committee2: '', committee3: '' });
        setSubmitError(null);
        reset(defaultValues);
        setView('form');
    }, [reset]);

    const handleBack = useCallback(() => {
        setView('selection');
        setSubmitError(null);
    }, []);

    const handleReset = useCallback(() => {
        setView('selection');
        setSubmitError(null);
        reset(defaultValues);
        setSelectedCommittees({ committee1: '', committee2: '', committee3: '' });
        localStorage.removeItem(DRAFT_KEY);
        setHasDraft(false);
        setDraftType(null);
    }, [reset]);

    const handleSaveDraft = useCallback(() => {
        const draft = { applicationType, formData: getValues(), selectedCommittees, savedAt: new Date().toISOString() };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
        toast.success(language === 'tr' ? 'Taslak kaydedildi!' : 'Draft saved!');
    }, [applicationType, getValues, selectedCommittees, language]);

    const handleContinueDraft = useCallback(() => {
        try {
            const saved = localStorage.getItem(DRAFT_KEY);
            if (saved) {
                const draft = JSON.parse(saved);
                setApplicationType(draft.applicationType);
                setSelectedCommittees(draft.selectedCommittees || { committee1: '', committee2: '', committee3: '' });
                reset(draft.formData);
                setHasDraft(false);
                setView('form');
                toast.success(language === 'tr' ? 'Taslak yüklendi!' : 'Draft loaded!');
            }
        } catch (e) {
            toast.error(language === 'tr' ? 'Taslak yüklenemedi' : 'Could not load draft');
        }
    }, [reset, language]);

    const handleDeleteDraft = useCallback(() => {
        localStorage.removeItem(DRAFT_KEY);
        setHasDraft(false);
        setDraftType(null);
        toast.success(language === 'tr' ? 'Taslak silindi' : 'Draft deleted');
    }, [language]);

    const handleCommitteeChange = useCallback((field, value) => {
        setSelectedCommittees(prev => ({ ...prev, [field]: value }));
        setValue(field, value);
    }, [setValue]);

    const getAvailableCommittees = useCallback((currentField) => {
        return committees.filter(c => {
            if (selectedCommittees[currentField] === c.name) return true;
            return !Object.entries(selectedCommittees).some(([f, v]) => f !== currentField && v === c.name);
        });
    }, [selectedCommittees]);

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        setSubmitError(null);
        try {
            const payload = {
                applicationType,
                firstName: data.firstName, lastName: data.lastName, email: data.email, phone: data.phone || '',
                dateOfBirth: data.dateOfBirth, city: data.city, district: data.district,
                schoolName: data.schoolName, grade: data.grade,
                parentName: data.parentName, parentPhone: data.parentPhone,
                healthInfo: { hasChronicIllness: data.hasChronicIllness === 'yes', allergies: data.allergies || '', emergencyMedications: data.emergencyMedications || '' },
                kvkkConsent: data.kvkkConsent, guardianConsent: data.guardianConsent,
                ...(applicationType === 'delegate' && { committeePreferences: [data.committee1, data.committee2, data.committee3] }),
                ...(applicationType === 'organization' && { position: data.position, previousConferences: data.previousConferences }),
                ...(applicationType === 'pr' && { previousConferences: data.previousConferences, motivationLetter: data.motivationLetter, hearAbout: data.hearAbout }),
                ...(applicationType === 'academy' && { conferenceCount: data.conferenceCount, roleType: data.roleType, conferenceList: data.conferenceList })
            };
            await submitApplication(payload);
            localStorage.removeItem(DRAFT_KEY);
            setHasDraft(false);
            setView('success');
        } catch (error) {
            setSubmitError(error.message || 'Başvuru gönderilirken bir hata oluştu');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Single stable render - NO Layout wrapper here
    return (
        <>
            <PageHeader view={view} applicationType={applicationType} onBack={handleBack} language={language} />
            
            <section className="py-16 bg-background min-h-[60vh]">
                {view === 'success' && <SuccessView onReset={handleReset} />}
                
                {view === 'selection' && (
                    <SelectionView 
                        onSelect={handleTypeSelect} 
                        hasDraft={hasDraft} 
                        draftType={draftType}
                        onContinueDraft={handleContinueDraft}
                        onDeleteDraft={handleDeleteDraft}
                        language={language}
                    />
                )}
                
                {view === 'form' && (
                    <div className="max-w-3xl mx-auto px-4">
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                            {/* Section 1: Personal Info */}
                            <div className="bg-card rounded-xl border border-border p-6 sm:p-8">
                                <h2 className="font-display text-2xl font-bold text-foreground mb-6 border-gold-accent pl-4">1. Kişisel Bilgiler</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div>
                                        <Label>Ad <span className="text-destructive">*</span></Label>
                                        <Input {...register('firstName')} className={cn("mt-2 input-elegant", errors.firstName && "border-destructive")} data-testid="input-firstName" />
                                        {errors.firstName && <p className="text-destructive text-sm mt-1">{errors.firstName.message}</p>}
                                    </div>
                                    <div>
                                        <Label>Soyad <span className="text-destructive">*</span></Label>
                                        <Input {...register('lastName')} className={cn("mt-2 input-elegant", errors.lastName && "border-destructive")} data-testid="input-lastName" />
                                        {errors.lastName && <p className="text-destructive text-sm mt-1">{errors.lastName.message}</p>}
                                    </div>
                                    <div>
                                        <Label>E-posta <span className="text-destructive">*</span></Label>
                                        <Input type="email" {...register('email')} className={cn("mt-2 input-elegant", errors.email && "border-destructive")} data-testid="input-email" />
                                        {errors.email && <p className="text-destructive text-sm mt-1">{errors.email.message}</p>}
                                    </div>
                                    <div>
                                        <Label>Telefon <span className="text-destructive">*</span></Label>
                                        <Input {...register('phone')} className={cn("mt-2 input-elegant", errors.phone && "border-destructive")} placeholder="+90 5XX XXX XX XX" data-testid="input-phone" />
                                        {errors.phone && <p className="text-destructive text-sm mt-1">{errors.phone.message}</p>}
                                    </div>
                                    <div>
                                        <Label>Doğum Tarihi <span className="text-destructive">*</span></Label>
                                        <Input type="date" {...register('dateOfBirth')} className={cn("mt-2 input-elegant", errors.dateOfBirth && "border-destructive")} data-testid="input-dateOfBirth" />
                                        {errors.dateOfBirth && <p className="text-destructive text-sm mt-1">{errors.dateOfBirth.message}</p>}
                                    </div>
                                    <div>
                                        <Label>Sınıf <span className="text-destructive">*</span></Label>
                                        <Select onValueChange={(v) => setValue('grade', v)}>
                                            <SelectTrigger className={cn("mt-2 input-elegant", errors.grade && "border-destructive")} data-testid="select-grade"><SelectValue placeholder="Sınıf seçin" /></SelectTrigger>
                                            <SelectContent>{grades.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
                                        </Select>
                                        {errors.grade && <p className="text-destructive text-sm mt-1">{errors.grade.message}</p>}
                                    </div>
                                    <div>
                                        <Label>Katılım İli <span className="text-destructive">*</span></Label>
                                        <Select onValueChange={(v) => setValue('city', v)}>
                                            <SelectTrigger className={cn("mt-2 input-elegant", errors.city && "border-destructive")} data-testid="select-city"><SelectValue placeholder="İl seçin" /></SelectTrigger>
                                            <SelectContent className="max-h-60">{turkeyProvinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                        </Select>
                                        {errors.city && <p className="text-destructive text-sm mt-1">{errors.city.message}</p>}
                                    </div>
                                    <div>
                                        <Label>İlçe <span className="text-destructive">*</span></Label>
                                        <Input {...register('district')} className={cn("mt-2 input-elegant", errors.district && "border-destructive")} data-testid="input-district" />
                                        {errors.district && <p className="text-destructive text-sm mt-1">{errors.district.message}</p>}
                                    </div>
                                    <div className="sm:col-span-2">
                                        <Label>Okul Adı <span className="text-destructive">*</span></Label>
                                        <Input {...register('schoolName')} className={cn("mt-2 input-elegant", errors.schoolName && "border-destructive")} data-testid="input-schoolName" />
                                        {errors.schoolName && <p className="text-destructive text-sm mt-1">{errors.schoolName.message}</p>}
                                    </div>
                                </div>
                            </div>

                            {/* Section 2: Type-specific */}
                            {applicationType === 'delegate' && (
                                <div className="bg-card rounded-xl border border-border p-6 sm:p-8">
                                    <h2 className="font-display text-2xl font-bold text-foreground mb-2 border-gold-accent pl-4">2. Komite Tercihleri</h2>
                                    <p className="text-muted-foreground text-sm mb-6 pl-4">Her tercih farklı olmalıdır.</p>
                                    <div className="space-y-4">
                                        {['committee1', 'committee2', 'committee3'].map((field, idx) => (
                                            <div key={field}>
                                                <Label>{idx + 1}. Tercih <span className="text-destructive">*</span></Label>
                                                <Select onValueChange={(v) => handleCommitteeChange(field, v)} value={selectedCommittees[field]}>
                                                    <SelectTrigger className={cn("mt-2 input-elegant", errors[field] && "border-destructive")} data-testid={`select-${field}`}><SelectValue placeholder={`${idx + 1}. tercihinizi seçin`} /></SelectTrigger>
                                                    <SelectContent>{getAvailableCommittees(field).map((c) => <SelectItem key={c.id} value={c.name}>{c.name} - {c.fullName}</SelectItem>)}</SelectContent>
                                                </Select>
                                                {errors[field] && <p className="text-destructive text-sm mt-1">{errors[field].message}</p>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {applicationType === 'organization' && (
                                <div className="bg-card rounded-xl border border-border p-6 sm:p-8">
                                    <h2 className="font-display text-2xl font-bold text-foreground mb-6 border-gold-accent pl-4">2. Organizasyon Bilgileri</h2>
                                    <div className="space-y-6">
                                        <div>
                                            <Label>Pozisyon <span className="text-destructive">*</span></Label>
                                            <Select onValueChange={(v) => setValue('position', v)}>
                                                <SelectTrigger className={cn("mt-2 input-elegant", errors.position && "border-destructive")} data-testid="select-position"><SelectValue placeholder="Pozisyon seçin" /></SelectTrigger>
                                                <SelectContent>{organizationPositions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                            </Select>
                                            {errors.position && <p className="text-destructive text-sm mt-1">{errors.position.message}</p>}
                                        </div>
                                        <div>
                                            <Label>Daha Önce Katıldığın Konferanslar</Label>
                                            <Textarea {...register('previousConferences')} className="mt-2 input-elegant min-h-[100px]" placeholder="Konferans isimlerini ve görevlerinizi yazın..." />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {applicationType === 'pr' && (
                                <div className="bg-card rounded-xl border border-border p-6 sm:p-8">
                                    <h2 className="font-display text-2xl font-bold text-foreground mb-6 border-gold-accent pl-4">2. PR Bilgileri</h2>
                                    <div className="space-y-6">
                                        <div>
                                            <Label>Daha Önce Katıldığın Konferanslar</Label>
                                            <Textarea {...register('previousConferences')} className="mt-2 input-elegant min-h-[100px]" placeholder="Konferans isimlerini yazın..." />
                                        </div>
                                        <div>
                                            <Label>Motivasyon Mektubu <span className="text-destructive">*</span></Label>
                                            <Textarea {...register('motivationLetter')} className={cn("mt-2 input-elegant min-h-[150px]", errors.motivationLetter && "border-destructive")} placeholder="Neden PR ekibinde yer almak istiyorsunuz?" />
                                            {errors.motivationLetter && <p className="text-destructive text-sm mt-1">{errors.motivationLetter.message}</p>}
                                        </div>
                                        <div>
                                            <Label>Bizi Nereden Duydunuz? <span className="text-destructive">*</span></Label>
                                            <Select onValueChange={(v) => setValue('hearAbout', v)}>
                                                <SelectTrigger className={cn("mt-2 input-elegant", errors.hearAbout && "border-destructive")}><SelectValue placeholder="Seçin" /></SelectTrigger>
                                                <SelectContent>{hearAboutOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                                            </Select>
                                            {errors.hearAbout && <p className="text-destructive text-sm mt-1">{errors.hearAbout.message}</p>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {applicationType === 'academy' && (
                                <div className="bg-card rounded-xl border border-border p-6 sm:p-8">
                                    <h2 className="font-display text-2xl font-bold text-foreground mb-6 border-gold-accent pl-4">2. Akademi Bilgileri</h2>
                                    <div className="space-y-6">
                                        <div>
                                            <Label>Konferansa Katılım Sayısı <span className="text-destructive">*</span></Label>
                                            <Select onValueChange={(v) => setValue('conferenceCount', v)}>
                                                <SelectTrigger className={cn("mt-2 input-elegant", errors.conferenceCount && "border-destructive")}><SelectValue placeholder="Seçin" /></SelectTrigger>
                                                <SelectContent>{conferenceCountOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                                            </Select>
                                            {errors.conferenceCount && <p className="text-destructive text-sm mt-1">{errors.conferenceCount.message}</p>}
                                        </div>
                                        <div>
                                            <Label>Görev Türü <span className="text-destructive">*</span></Label>
                                            <Select onValueChange={(v) => setValue('roleType', v)}>
                                                <SelectTrigger className={cn("mt-2 input-elegant", errors.roleType && "border-destructive")}><SelectValue placeholder="Seçin" /></SelectTrigger>
                                                <SelectContent>{academyRoles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                                            </Select>
                                            {errors.roleType && <p className="text-destructive text-sm mt-1">{errors.roleType.message}</p>}
                                        </div>
                                        <div>
                                            <Label>Katıldığın Konferanslar</Label>
                                            <Textarea {...register('conferenceList')} className="mt-2 input-elegant min-h-[100px]" placeholder="Konferans isimlerini, tarihlerini ve görevlerinizi yazın..." />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Section 3: Parent Info */}
                            <div className="bg-card rounded-xl border border-border p-6 sm:p-8">
                                <h2 className="font-display text-2xl font-bold text-foreground mb-6 border-gold-accent pl-4">3. Veli / Yasal Vasi Bilgileri</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div className="sm:col-span-2">
                                        <Label>Veli Ad Soyad <span className="text-destructive">*</span></Label>
                                        <Input {...register('parentName')} className={cn("mt-2 input-elegant", errors.parentName && "border-destructive")} data-testid="input-parentName" />
                                        {errors.parentName && <p className="text-destructive text-sm mt-1">{errors.parentName.message}</p>}
                                    </div>
                                    <div className="sm:col-span-2">
                                        <Label>Veli Telefon <span className="text-destructive">*</span></Label>
                                        <Input {...register('parentPhone')} className={cn("mt-2 input-elegant", errors.parentPhone && "border-destructive")} placeholder="+90 5XX XXX XX XX" data-testid="input-parentPhone" />
                                        {errors.parentPhone && <p className="text-destructive text-sm mt-1">{errors.parentPhone.message}</p>}
                                    </div>
                                </div>
                            </div>

                            {/* Section 4: Health Info */}
                            <div className="bg-card rounded-xl border border-border p-6 sm:p-8">
                                <div className="flex items-start gap-3 mb-6">
                                    <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0">
                                        <ShieldAlert className="w-5 h-5 text-orange-500" />
                                    </div>
                                    <div>
                                        <h2 className="font-display text-2xl font-bold text-foreground">4. Sağlık Bilgileri</h2>
                                        <p className="text-muted-foreground text-sm mt-1"><span className="font-semibold text-orange-500">GİZLİ</span> — Bu bilgiler sadece acil durumlar için kullanılacaktır.</p>
                                    </div>
                                </div>
                                <div className="space-y-6">
                                    <div>
                                        <Label className="mb-3 block">Kronik hastalığınız var mı? <span className="text-destructive">*</span></Label>
                                        <RadioGroup onValueChange={(v) => setValue('hasChronicIllness', v)} className="flex gap-6">
                                            <div className="flex items-center space-x-2"><RadioGroupItem value="yes" id="chronic-yes" /><Label htmlFor="chronic-yes" className="cursor-pointer">Evet</Label></div>
                                            <div className="flex items-center space-x-2"><RadioGroupItem value="no" id="chronic-no" /><Label htmlFor="chronic-no" className="cursor-pointer">Hayır</Label></div>
                                        </RadioGroup>
                                        {errors.hasChronicIllness && <p className="text-destructive text-sm mt-1">{errors.hasChronicIllness.message}</p>}
                                    </div>
                                    <div>
                                        <Label>Alerjiler <span className="text-muted-foreground text-sm">(varsa)</span></Label>
                                        <Textarea {...register('allergies')} className="mt-2 input-elegant min-h-[80px]" placeholder="Alerji bilgilerinizi yazın..." />
                                    </div>
                                    <div>
                                        <Label>Acil Durum İlaçları <span className="text-muted-foreground text-sm">(varsa)</span></Label>
                                        <Textarea {...register('emergencyMedications')} className="mt-2 input-elegant min-h-[80px]" placeholder="Kullandığınız ilaçları yazın..." />
                                    </div>
                                </div>
                            </div>

                            {/* Section 5: Consent */}
                            <div className="bg-card rounded-xl border border-border p-6 sm:p-8">
                                <h2 className="font-display text-2xl font-bold text-foreground mb-6 border-gold-accent pl-4">5. Yasal Onaylar</h2>
                                <div className="space-y-6">
                                    <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                                        <Checkbox id="kvkkConsent" onCheckedChange={(c) => setValue('kvkkConsent', c)} data-testid="checkbox-kvkkConsent" />
                                        <div className="flex-1">
                                            <Label htmlFor="kvkkConsent" className="cursor-pointer leading-relaxed">KVKK Aydınlatma Metni'ni okudum ve kabul ediyorum. <span className="text-destructive">*</span></Label>
                                            {errors.kvkkConsent && <p className="text-destructive text-sm mt-2">{errors.kvkkConsent.message}</p>}
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                                        <Checkbox id="guardianConsent" onCheckedChange={(c) => setValue('guardianConsent', c)} data-testid="checkbox-guardianConsent" />
                                        <div className="flex-1">
                                            <Label htmlFor="guardianConsent" className="cursor-pointer leading-relaxed">Yasal vasi olarak bu başvuruyu onaylıyorum. <span className="text-destructive">*</span></Label>
                                            {errors.guardianConsent && <p className="text-destructive text-sm mt-2">{errors.guardianConsent.message}</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {submitError && (
                                <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                                    <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
                                    <p className="text-destructive">{submitError}</p>
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row justify-between gap-4">
                                <Button type="button" variant="outline" onClick={handleSaveDraft} className="flex items-center gap-2 border-accent/50 text-accent hover:bg-accent/10" data-testid="save-draft-btn">
                                    <Save className="w-4 h-4" />{language === 'tr' ? 'Taslak Kaydet' : 'Save Draft'}
                                </Button>
                                <Button type="submit" disabled={isSubmitting} className="btn-premium px-8 py-6 text-lg font-semibold rounded-lg text-primary min-w-[200px]" data-testid="submit-application">
                                    {isSubmitting ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" />Gönderiliyor...</span> : <span>{language === 'tr' ? 'Başvuruyu Gönder' : 'Submit Application'}</span>}
                                </Button>
                            </div>
                        </form>
                    </div>
                )}
            </section>
        </>
    );
};

export default ApplyPage;
